import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Users,
  UserCog,
  Building2,
  Package,
  ShoppingCart,
  Wrench,
  Webhook,
  History,
  Sparkles,
  Receipt,
  Wallet,
  Warehouse,
  Factory,
  MapPin,
  Radio,
  Route,
  Stethoscope,
  Thermometer,
  UtensilsCrossed,
  Sprout,
  HardHat,
  Settings,
  BarChart3,
  MessageSquare,
  ShieldCheck,
  Puzzle,
  Briefcase,
  Timer,
  Tag,
  Target,
  Truck,
  Car,
  Package2,
  Printer,
  Banknote,
  Landmark,
  FileCheck2,
  Store,
  Bell,
  LifeBuoy,
  BookOpen,
  BookText,
  Scale,
  Coins,
  CalendarRange,
  ReceiptText,
  Building,
  PiggyBank,
  Percent,
  ShoppingBag,
  ClipboardList,
  FileText,
  Handshake,
  DoorOpen,
  PackageCheck,
  Boxes,
  MessagesSquare,
  TrendingDown,
  ScanSearch,
  Bot,
  Trophy,
  Gift,
} from 'lucide-react'

/**
 * Navigation tree. Each leaf declares the permission + module it requires;
 * the sidebar renders an item only if useCanAccess passes. Section groups
 * hide entirely when all children are hidden.
 *
 * This is the single source of truth for portal navigation. As modules are
 * built batch-by-batch, their routes get added here.
 */

export interface NavLeaf {
  type: 'leaf'
  label: string
  /** Optional terminology key — overrides label via useTerm() when present. */
  termKey?: string
  to: string
  icon?: LucideIcon
  permission?: string | string[]
  module?: string | string[]
  /** Visible if ANY one of these modules is enabled (e.g. gateway_* family). */
  moduleAny?: string[]
  /** DistroOps internal admin item — gated by a platform.* permission via useCanPlatform */
  platform?: boolean
}

export interface NavSection {
  type: 'section'
  label: string
  icon?: LucideIcon
  children: NavLeaf[]
}

export type NavItem = NavSection | NavLeaf

export const navigation: NavItem[] = [
  {
    type: 'leaf',
    label: 'Dashboard',
    to: '/',
    icon: LayoutDashboard,
  },
  {
    type: 'section',
    label: 'Marketplace',
    icon: Store,
    children: [
      {
        type: 'leaf',
        label: 'Browse',
        to: '/marketplace',
        icon: Store,
        permission: 'marketplace.browse',
        module: 'marketplace',
      },
      {
        type: 'leaf',
        label: 'Marketplace Orders',
        to: '/marketplace/orders',
        icon: ShoppingCart,
        permission: 'marketplace.orders.view',
        module: 'marketplace',
      },
      {
        type: 'leaf',
        label: 'Auto-Reorder',
        to: '/marketplace/auto-reorder',
        icon: Timer,
        permission: 'marketplace.recurring.manage',
        module: 'marketplace',
      },
      {
        type: 'leaf',
        label: 'My Listings',
        to: '/marketplace/listings',
        icon: Tag,
        permission: 'marketplace.listings.manage',
        module: 'marketplace',
      },
    ],
  },
  {
    type: 'section',
    label: 'Sales & CRM',
    icon: ShoppingCart,
    children: [
      {
        type: 'leaf',
        label: 'Customers',
        termKey: 'customers',
        to: '/customers',
        icon: Users,
        permission: 'customers.view',
        module: 'customers',
      },
      {
        type: 'leaf',
        label: 'Segments',
        to: '/customer-segments',
        permission: 'customers.view',
        module: 'customer_segmentation',
      },
      {
        type: 'leaf',
        label: 'Leads',
        to: '/leads',
        permission: 'leads.view',
        module: 'leads',
      },
      {
        type: 'leaf',
        label: 'Orders',
        termKey: 'orders',
        to: '/orders',
        permission: 'orders.view',
        module: 'orders',
      },
      {
        type: 'leaf',
        label: 'Control Tower',
        to: '/secondary-sales',
        permission: 'secondary_sales.view',
        module: 'secondary_sales',
      },
      {
        type: 'leaf',
        label: 'Scheme Claims',
        to: '/scheme-claims',
        permission: 'claims.view',
        module: 'claims',
      },
      {
        type: 'leaf',
        label: 'Demand Planning',
        to: '/demand-planning',
        permission: 'forecasting.view',
        module: 'forecasting',
      },
      {
        type: 'leaf',
        label: 'Retail Execution',
        to: '/merchandising',
        permission: 'merchandising.view',
        module: 'merchandising',
      },
      {
        type: 'leaf',
        label: 'Merchandising Setup',
        to: '/merchandising/setup',
        permission: 'merchandising.manage',
        module: 'merchandising',
      },
      {
        type: 'leaf',
        label: 'Van Sales',
        to: '/van-sales',
        permission: 'van_sales.view',
        module: 'van_sales',
      },
      {
        type: 'leaf',
        label: 'Recurring Orders',
        to: '/recurring-orders',
        permission: 'orders.view',
        module: 'recurring_orders',
      },
      {
        type: 'leaf',
        label: 'Service Quotes',
        to: '/service-quotes',
        permission: 'quotes.view',
        module: 'service_quotes',
      },
      {
        type: 'leaf',
        label: 'Quotations',
        to: '/quotations',
        permission: 'sales.quotes.manage',
        module: 'sales_quotes',
      },
      {
        type: 'leaf',
        label: 'Opportunities',
        to: '/crm/opportunities',
        icon: Handshake,
        permission: 'crm.pipeline.manage',
        module: 'crm_pipeline',
      },
      {
        type: 'leaf',
        label: 'Targets',
        to: '/sales-targets',
        icon: Target,
        permission: 'sales_targets.view',
        module: 'sales_targets',
      },
      {
        type: 'leaf',
        label: 'Commissions',
        to: '/commissions',
        icon: Wallet,
        permission: 'commissions.view',
        module: 'commissions',
      },
    ],
  },
  {
    type: 'section',
    label: 'Catalog & Inventory',
    icon: Package,
    children: [
      {
        type: 'leaf',
        label: 'Products',
        termKey: 'products',
        to: '/products',
        icon: Package,
        permission: 'products.view',
        module: 'products',
      },
      {
        type: 'leaf',
        label: 'Categories',
        to: '/product-categories',
        permission: 'products.view',
        module: 'categories',
      },
      {
        type: 'leaf',
        label: 'Brands',
        to: '/brands',
        permission: 'products.view',
        module: 'products',
      },
      {
        type: 'leaf',
        label: 'Units of Measure',
        to: '/units',
        permission: 'products.view',
        module: 'products',
      },
      {
        type: 'leaf',
        label: 'Stock Levels',
        to: '/stock',
        icon: Warehouse,
        permission: 'products.view',
        module: 'stock_management',
      },
      {
        type: 'leaf',
        label: 'Warehouses',
        to: '/warehouses',
        icon: Warehouse,
        permission: 'warehouses.view',
        module: 'warehouses',
      },
      {
        type: 'leaf',
        label: 'Bin Locations',
        to: '/warehouses/bin-locations',
        icon: Boxes,
        permission: 'bin_locations.manage',
        module: 'bin_locations',
      },
      {
        type: 'leaf',
        label: 'Slow-moving Stock',
        to: '/reports/slow-moving',
        icon: TrendingDown,
        permission: 'bin_locations.manage',
      },
      {
        type: 'leaf',
        label: 'Stock Transfers',
        to: '/stock-transfers',
        permission: 'stock_transfers.view',
        module: 'stock_transfers',
      },
      {
        type: 'leaf',
        label: 'Stock Adjustments',
        to: '/stock-adjustments',
        permission: 'stock_adjustments.view',
        module: 'stock_management',
      },
      {
        type: 'leaf',
        label: 'Stock Movements',
        to: '/stock-movements',
        permission: 'products.view',
        module: 'stock_management',
      },
    ],
  },
  {
    type: 'section',
    label: 'Manufacturing',
    icon: Factory,
    children: [
      {
        type: 'leaf',
        label: 'Work Orders',
        to: '/manufacturing/work-orders',
        icon: Factory,
        permission: 'manufacturing.view',
        module: 'manufacturing',
      },
      {
        type: 'leaf',
        label: 'Bills of Material',
        to: '/manufacturing/boms',
        icon: Boxes,
        permission: 'manufacturing.view',
        module: 'manufacturing',
      },
      {
        type: 'leaf',
        label: 'Work Centers',
        to: '/manufacturing/work-centers',
        icon: Wrench,
        permission: 'manufacturing.view',
        module: 'manufacturing',
      },
    ],
  },
  {
    type: 'section',
    label: 'Pricing',
    icon: Tag,
    children: [
      {
        type: 'leaf',
        label: 'Price Lists',
        to: '/price-lists',
        icon: Tag,
        permission: 'price_lists.view',
        module: 'price_lists',
      },
      {
        type: 'leaf',
        label: 'Pricing Rules',
        to: '/pricing-rules',
        permission: 'pricing.view',
        module: 'pricing_rules',
      },
      {
        type: 'leaf',
        label: 'Schemes',
        to: '/schemes',
        permission: 'schemes.view',
        module: 'schemes',
      },
    ],
  },
  {
    type: 'section',
    label: 'Finance',
    icon: Wallet,
    children: [
      {
        type: 'leaf',
        label: 'Invoices',
        termKey: 'invoices',
        to: '/invoices',
        icon: Receipt,
        permission: 'invoices.view',
        module: 'invoices',
      },
      {
        type: 'leaf',
        label: 'Payments',
        to: '/payments',
        permission: 'payments.view',
        module: 'payments',
      },
      {
        type: 'leaf',
        label: 'Receivables',
        to: '/receivables/aging',
        permission: 'payments.view',
        module: 'receivables',
      },
      {
        type: 'leaf',
        label: 'Credit Notes',
        to: '/credit-notes',
        permission: 'credit_notes.view',
        module: 'credit_notes',
      },
      {
        type: 'leaf',
        label: 'Debit Notes',
        to: '/debit-notes',
        permission: 'debit_notes.view',
        module: 'debit_notes',
      },
      {
        type: 'leaf',
        label: 'Sales Returns',
        to: '/sales-returns',
        permission: 'sales_returns.view',
        module: 'sales_returns',
      },
      {
        type: 'leaf',
        label: 'Refunds',
        to: '/refunds',
        permission: 'refunds.view',
        module: 'refunds',
      },
      {
        type: 'leaf',
        label: 'COD Handovers',
        to: '/finance/cod-handovers',
        icon: Banknote,
        permission: 'cod.view',
        module: 'cod_handovers',
      },
      {
        type: 'leaf',
        label: 'Bank Imports',
        to: '/finance/bank-imports',
        icon: Landmark,
        permission: 'cod.view',
        module: 'bank_statement_imports',
      },
      {
        type: 'leaf',
        label: 'Bank Settings',
        to: '/bank-settings',
        permission: 'payments.update',
        module: 'bank_accounts',
      },
    ],
  },
  {
    type: 'section',
    label: 'Accounting',
    icon: BookOpen,
    children: [
      {
        type: 'leaf',
        label: 'Chart of Accounts',
        to: '/accounting/chart-of-accounts',
        icon: BookText,
        permission: 'accounting.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Journal Entries',
        to: '/accounting/journal-entries',
        icon: BookOpen,
        permission: 'accounting.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Financial Statements',
        to: '/accounting/statements',
        icon: BarChart3,
        permission: 'accounting.statements.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Reconciliation',
        to: '/accounting/reconciliation',
        icon: Scale,
        permission: 'accounting.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Accounting Periods',
        to: '/accounting/periods',
        icon: CalendarRange,
        permission: 'accounting.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Currencies & FX',
        to: '/accounting/currencies',
        icon: Coins,
        permission: 'accounting.view',
        module: 'accounting',
      },
      {
        type: 'leaf',
        label: 'Receivables',
        to: '/accounting/receivables',
        icon: Wallet,
        permission: 'payments.view',
        module: 'payments',
      },
      {
        type: 'leaf',
        label: 'COD Reconciliation',
        to: '/accounting/cod-reconciliation',
        icon: Banknote,
        permission: 'cod_handover.view',
        module: 'payments',
      },
      {
        type: 'leaf',
        label: 'COD Handovers',
        to: '/accounting/cod-handovers',
        icon: Banknote,
        permission: 'cod_handover.view',
        module: 'payments',
      },
      {
        type: 'leaf',
        label: 'Expenses',
        to: '/accounting/expenses',
        icon: ReceiptText,
        permission: 'expenses.view',
        module: 'expenses',
      },
      {
        type: 'leaf',
        label: 'Fixed Assets',
        to: '/accounting/fixed-assets',
        icon: Building,
        permission: 'fixed_assets.view',
        module: 'fixed_assets',
      },
      {
        type: 'leaf',
        label: 'Budgeting',
        to: '/accounting/budgets',
        icon: PiggyBank,
        permission: 'budgeting.view',
        module: 'budgeting',
      },
      {
        type: 'leaf',
        label: 'Withholding Tax',
        to: '/accounting/withholding-tax',
        icon: Percent,
        permission: 'withholding_tax.view',
        module: 'withholding_tax',
      },
    ],
  },
  {
    type: 'leaf',
    label: 'Point of Sale',
    to: '/pos',
    icon: ShoppingBag,
    permission: 'pos.operate',
    module: 'pos',
  },
  {
    type: 'leaf',
    label: 'Messages',
    to: '/messages',
    icon: MessagesSquare,
    permission: 'messaging.use',
    module: 'internal_messaging',
  },
  {
    type: 'section',
    label: 'Intelligence',
    icon: Sparkles,
    children: [
      {
        type: 'leaf',
        label: 'AI Auditor',
        to: '/ai/auditor',
        icon: ScanSearch,
        permission: 'ai.auditor.view',
        module: 'ai_auditor',
      },
      {
        type: 'leaf',
        label: 'AI Analyst',
        to: '/ai/analyst',
        icon: Sparkles,
        permission: 'ai.analyst.view',
        module: 'ai_analyst',
      },
      {
        type: 'leaf',
        label: 'AI Assistant',
        to: '/ai/assistant',
        icon: Bot,
        permission: 'ai.assistant.use',
        module: 'ai_assistant',
      },
      {
        type: 'leaf',
        label: 'Nudges & MSL',
        to: '/intelligence',
        icon: Sparkles,
        permission: 'intelligence.view',
        module: 'intelligence',
      },
    ],
  },
  {
    type: 'section',
    label: 'Engagement',
    icon: Trophy,
    children: [
      {
        type: 'leaf',
        label: 'Gamification',
        to: '/gamification',
        icon: Trophy,
        permission: 'gamification.view',
        module: 'gamification',
      },
      {
        type: 'leaf',
        label: 'Loyalty',
        to: '/loyalty',
        icon: Gift,
        permission: 'loyalty.view',
        module: 'loyalty',
      },
    ],
  },
  {
    type: 'section',
    label: 'Purchasing',
    icon: ShoppingCart,
    children: [
      {
        type: 'leaf',
        label: 'Requisitions',
        to: '/procurement/requisitions',
        icon: ClipboardList,
        permission: 'procurement.requisitions.manage',
        module: 'procurement_requisitions',
      },
      {
        type: 'leaf',
        label: 'RFQs',
        to: '/procurement/rfqs',
        icon: FileText,
        permission: 'procurement.rfq.manage',
        module: 'rfq',
      },
      {
        type: 'leaf',
        label: 'Suppliers',
        to: '/suppliers',
        permission: 'suppliers.view',
        module: 'purchases',
      },
      {
        type: 'leaf',
        label: 'Supplier Ledger',
        to: '/procurement/supplier-ledger',
        permission: 'suppliers.view',
        module: 'purchases',
      },
      {
        type: 'leaf',
        label: 'Purchase Orders',
        to: '/purchase-orders',
        permission: 'purchase_orders.view',
        module: 'purchases',
      },
      {
        type: 'leaf',
        label: 'Goods Receipts',
        to: '/goods-receipts',
        permission: 'goods_receipts.view',
        module: 'purchases',
      },
      {
        type: 'leaf',
        label: 'Purchase Returns',
        to: '/purchase-returns',
        permission: 'purchase_returns.view',
        module: 'purchases',
      },
    ],
  },
  {
    type: 'section',
    label: 'Field Operations',
    icon: MapPin,
    children: [
      {
        type: 'leaf',
        label: 'Sales Reps',
        termKey: 'reps',
        to: '/field-force',
        icon: Users,
        permission: 'field_force.view',
        module: 'field_force',
      },
      {
        type: 'leaf',
        label: 'Journey Plans',
        to: '/journey-plans',
        permission: 'journey_plans.view',
        module: 'field_force',
      },
      {
        type: 'leaf',
        label: 'Attendance',
        to: '/attendance',
        permission: 'attendance.view',
        module: 'field_force',
      },
      {
        type: 'leaf',
        label: 'Live Tracking',
        to: '/live-tracking',
        icon: Radio,
        permission: 'attendance.view',
        module: 'gps_tracking',
      },
      {
        type: 'leaf',
        label: 'Dispatch Board',
        to: '/dispatch',
        icon: Route,
        permission: 'delivery_runs.view',
        module: 'dispatch_board',
      },
      {
        type: 'leaf',
        label: 'Delivery Runs',
        to: '/delivery-runs',
        icon: Truck,
        permission: 'delivery_runs.view',
        module: 'delivery_runs',
      },
      {
        type: 'leaf',
        label: 'Gate Passes',
        to: '/logistics/gate-passes',
        icon: DoorOpen,
        permission: 'gate_passes.manage',
        module: 'gate_passes',
      },
      {
        type: 'leaf',
        label: 'Delivery Challans',
        to: '/logistics/delivery-challans',
        icon: PackageCheck,
        permission: 'delivery_challans.manage',
        module: 'delivery_challans',
      },
      {
        type: 'leaf',
        label: 'Picking',
        to: '/pick-lists',
        icon: Package,
        permission: 'pick_pack.view',
        module: 'pick_pack',
      },
      {
        type: 'leaf',
        label: 'Drivers',
        to: '/drivers',
        icon: UserCog,
        permission: 'drivers.view',
        module: 'drivers',
      },
      {
        type: 'leaf',
        label: 'Vehicles',
        to: '/vehicles',
        icon: Car,
        permission: 'vehicles.view',
        module: 'vehicles',
      },
      {
        type: 'leaf',
        label: 'Driver Pay',
        to: '/driver-pay',
        icon: Banknote,
        permission: 'drivers.view',
        module: 'delivery_runs',
      },
      {
        type: 'leaf',
        label: 'Driver COD & Shifts',
        to: '/driver-operations',
        icon: Wallet,
        permission: 'drivers.view',
        module: 'delivery_runs',
      },
      {
        type: 'leaf',
        label: 'Pallets',
        to: '/pallets',
        icon: Package2,
        permission: 'pallets.view',
        module: 'pallets',
      },
      {
        type: 'leaf',
        label: 'Shipment Labels',
        to: '/shipment-labels',
        icon: Printer,
        permission: 'label_template.view',
        module: 'shipment_label_templates',
      },
      {
        type: 'leaf',
        label: 'Territories',
        termKey: 'territories',
        to: '/territories',
        icon: MapPin,
        permission: 'field_force.view',
        module: 'beats',
      },
      {
        type: 'leaf',
        label: 'Territory Map',
        to: '/territories/map',
        icon: MapPin,
        permission: 'field_force.view',
        module: 'beats',
      },
      {
        type: 'leaf',
        label: 'Coverage',
        to: '/territories/coverage',
        icon: MapPin,
        permission: 'field_force.view',
        module: 'beats',
      },
      {
        type: 'leaf',
        label: 'Beats',
        termKey: 'beats',
        to: '/beats',
        icon: Route,
        permission: 'field_force.view',
        module: 'beats',
      },
      {
        type: 'leaf',
        label: 'Visits',
        termKey: 'visits',
        to: '/visits',
        icon: MapPin,
        permission: 'visits.view',
        module: 'visits',
      },
      {
        type: 'leaf',
        label: 'Cooldown Rules',
        termKey: 'visit_cooldown_rules',
        to: '/visit-cooldown-rules',
        icon: Timer,
        permission: 'visit_cooldown_rules.view',
        module: 'visit_cooldown_rules',
      },
      {
        type: 'leaf',
        label: 'Override Requests',
        termKey: 'visit_overrides',
        to: '/visit-overrides',
        icon: ShieldCheck,
        permission: 'visit_overrides.view',
        module: 'visit_overrides',
      },
    ],
  },
  {
    type: 'section',
    label: 'Field Service',
    icon: Wrench,
    children: [
      {
        type: 'leaf',
        label: 'Service Tickets',
        to: '/service-tickets',
        permission: 'service_tickets.view',
        module: 'service_tickets',
      },
      {
        type: 'leaf',
        label: 'Technicians',
        to: '/technicians',
        permission: 'technicians.view',
        module: 'technicians',
      },
      {
        type: 'leaf',
        label: 'Skills',
        to: '/skills',
        permission: 'skills.view',
        module: 'service_tickets',
      },
      {
        type: 'leaf',
        label: 'SLA Policies',
        to: '/sla-policies',
        permission: 'sla_policies.view',
        module: 'sla_management',
      },
      {
        type: 'leaf',
        label: 'Customer Assets',
        to: '/customer-assets',
        permission: 'customer_assets.view',
        module: 'service_tickets',
      },
      {
        type: 'leaf',
        label: 'Checklist Templates',
        to: '/checklist-templates',
        permission: 'checklist_templates.view',
        module: 'service_tickets',
      },
      {
        type: 'leaf',
        label: 'Knowledge Base',
        to: '/knowledge-base',
        permission: 'kb_articles.view',
        module: 'service_tickets',
      },
      {
        type: 'leaf',
        label: 'Service Analytics',
        to: '/field-service/analytics',
        permission: 'service_tickets.view',
        module: 'service_tickets',
      },
    ],
  },
  {
    type: 'section',
    label: 'Verticals',
    icon: Stethoscope,
    children: [
      {
        type: 'leaf',
        label: 'Pharma',
        to: '/verticals/pharma',
        icon: Stethoscope,
        permission: 'pharma.view',
        module: 'doctor_calls',
      },
      {
        type: 'leaf',
        label: 'Pharma Batches',
        to: '/verticals/pharma/batches',
        icon: Stethoscope,
        permission: 'pharma.batches.view',
        module: 'batches',
      },
      {
        type: 'leaf',
        label: 'Samples',
        to: '/samples',
        icon: Stethoscope,
        permission: 'visits.view',
        module: 'samples_management',
      },
      {
        type: 'leaf',
        label: 'Cold Chain',
        to: '/verticals/cold-chain',
        icon: Thermometer,
        permission: 'cold_chain.view',
        module: 'cold_chain',
      },
      {
        type: 'leaf',
        label: 'HORECA',
        to: '/verticals/horeca',
        icon: UtensilsCrossed,
        permission: 'horeca.view',
        module: 'horeca_management',
      },
      {
        type: 'leaf',
        label: 'Agriculture',
        to: '/verticals/agriculture',
        icon: Sprout,
        permission: 'agriculture.view',
        module: 'agriculture',
      },
      {
        type: 'leaf',
        label: 'Construction',
        to: '/verticals/construction',
        icon: HardHat,
        permission: 'construction.view',
        module: 'construction',
      },
      {
        type: 'leaf',
        label: 'Construction BOQ/Claims',
        to: '/verticals/construction/tools',
        icon: HardHat,
        permission: 'construction.view',
        module: 'boq_management',
      },
      {
        type: 'leaf',
        label: 'Auto Parts',
        to: '/verticals/auto-parts',
        icon: Stethoscope,
        permission: 'auto_parts.view',
        module: 'auto_parts',
      },
      {
        type: 'leaf',
        label: 'Modern Trade',
        to: '/verticals/modern-trade',
        icon: Stethoscope,
        permission: 'modern_trade.view',
        module: 'modern_trade',
      },
      {
        type: 'leaf',
        label: 'Pharma DRAP Reports',
        to: '/verticals/pharma/drap-reports',
        icon: Stethoscope,
        permission: 'pharma.view',
        module: 'drap_compliance',
      },
      {
        type: 'leaf',
        label: 'Pharma Rx Audit',
        to: '/verticals/pharma/rx-audit',
        icon: Stethoscope,
        permission: 'pharma.view',
        module: 'rx_audit',
      },
      {
        type: 'leaf',
        label: 'Pharma Prescriptions',
        to: '/verticals/pharma/prescriptions',
        icon: Stethoscope,
        permission: 'pharma.view',
        module: 'prescriptions',
      },
      {
        type: 'leaf',
        label: 'Pharma Brand Performance',
        to: '/verticals/pharma/brand-performance',
        icon: Stethoscope,
        permission: 'pharma.view',
        module: 'brand_performance',
      },
      {
        type: 'leaf',
        label: 'Cold Chain Custody',
        to: '/verticals/cold-chain/custody',
        icon: HardHat,
        permission: 'cold_chain.view',
        module: 'cold_chain_custody',
      },
      {
        type: 'leaf',
        label: 'Agriculture Tools',
        to: '/verticals/agriculture/tools',
        icon: Sprout,
        permission: 'agriculture.view',
        module: 'ag_advisory',
      },
      {
        type: 'leaf',
        label: 'HORECA Tools',
        to: '/verticals/horeca/tools',
        icon: HardHat,
        permission: 'horeca.view',
        module: 'horeca_recipes',
      },
    ],
  },
  {
    type: 'section',
    label: 'Reports',
    icon: BarChart3,
    children: [
      {
        type: 'leaf',
        label: 'Reports',
        to: '/reports', module: 'reports',
        icon: BarChart3,
        permission: 'reports.view',
      },
    ],
  },
  {
    type: 'section',
    label: 'Communications',
    icon: MessageSquare,
    children: [
      {
        type: 'leaf',
        label: 'Messaging',
        to: '/communications',
        icon: MessageSquare,
        permission: 'broadcasts.view',
        module: 'broadcasts',
      },
    ],
  },
  {
    type: 'section',
    label: 'Administration',
    icon: Settings,
    children: [
      {
        type: 'leaf',
        label: 'Users & Team',
        to: '/users',
        icon: Users,
        permission: 'users.view',
      },
      {
        type: 'leaf',
        label: 'Invitations',
        to: '/invitations', 
        permission: 'users.create',
      },
      {
        type: 'leaf',
        label: 'Roles',
        to: '/roles',
        icon: UserCog,
        permission: 'roles.view',
      },
      {
        type: 'leaf',
        label: 'Company Settings',
        to: '/settings',
        icon: Building2,
        permission: 'company_settings.view',
      },
      {
        type: 'leaf',
        label: 'Subscription',
        to: '/subscription',
        icon: Wallet,
        permission: 'subscription.view',
      },
      {
        type: 'leaf',
        label: 'WhatsApp',
        to: '/whatsapp',
        icon: MessageSquare,
        permission: 'whatsapp.templates.view',
        module: 'whatsapp_business',
      },
      {
        type: 'leaf',
        label: 'Webhooks',
        to: '/webhooks',
        icon: Webhook,
        permission: 'webhooks.view',
        module: 'webhooks',
      },
      {
        type: 'leaf',
        label: 'FBR e-Invoicing',
        to: '/fbr',
        icon: Receipt,
        permission: 'fbr.view',
        module: 'fbr',
      },
      {
        type: 'leaf',
        label: 'ZATCA Config',
        to: '/tax/zatca/config',
        icon: ShieldCheck,
        permission: 'zatca.view',
        module: 'zatca_config',
      },
      {
        type: 'leaf',
        label: 'ZATCA E-Invoices',
        to: '/tax/zatca/einvoices',
        icon: FileCheck2,
        permission: 'zatca.view',
        module: 'zatca_einvoices',
      },
      {
        type: 'leaf',
        label: 'E-commerce Stores',
        to: '/ecommerce/stores',
        icon: Store,
        permission: 'ecommerce.view',
        module: 'ecommerce_stores',
      },
      {
        type: 'leaf',
        label: 'Notifications',
        to: '/notifications-inbox',
        icon: Bell,
      },
      {
        type: 'leaf',
        label: 'Custom Fields',
        to: '/admin/custom-fields', module: 'custom_fields',
        permission: 'custom_fields.view',
      },
      {
        type: 'leaf',
        label: 'Workflows',
        to: '/admin/workflows', module: 'workflows',
        permission: 'workflows.view',
      },
      {
        type: 'leaf',
        label: 'Custom Reports',
        to: '/admin/custom-reports', module: 'custom_reports',
        permission: 'custom_reports.view',
      },
      {
        type: 'leaf',
        label: 'Forms',
        to: '/admin/forms', module: 'custom_forms',
        permission: 'forms.view',
      },
      {
        type: 'leaf',
        label: 'Document Templates',
        to: '/admin/document-templates', module: 'document_templates',
        permission: 'document_templates.view',
      },
      {
        type: 'leaf',
        label: 'Notification Rules',
        to: '/admin/notification-rules', module: 'notification_rules',
        permission: 'notification_rules.view',
      },
      {
        type: 'leaf',
        label: 'Alert Rules',
        to: '/admin/alert-rules', module: 'notification_rules',
        permission: 'alert_rules.view',
      },
      {
        type: 'leaf',
        label: 'WhatsApp Templates',
        to: '/admin/whatsapp-templates', module: 'whatsapp_business',
        permission: 'whatsapp.templates.view',
      },
      {
        type: 'leaf',
        label: 'FBR Settings',
        to: '/admin/fbr-settings', module: 'fbr',
        permission: 'fbr.configure',
      },
      {
        type: 'leaf',
        label: 'API Tokens',
        to: '/admin/api-tokens', module: 'public_api',
        permission: 'api_tokens.view',
      },
      {
        type: 'leaf',
        label: 'Custom Roles',
        to: '/admin/custom-roles', module: 'custom_roles',
        permission: 'roles.view',
      },
      {
        type: 'leaf',
        label: 'Quotas & Overages',
        to: '/admin/quotas', module: 'quotas',
        permission: 'subscription.view',
      },
      {
        type: 'leaf',
        label: 'Service Contracts',
        to: '/service-contracts',
        permission: 'contracts.view',
        module: 'service_contracts',
      },
      {
        type: 'leaf',
        label: 'Customization Audit',
        to: '/admin/customization-audit', module: 'audit_query',
        permission: 'audit.view',
      },
      {
        type: 'leaf',
        label: 'EDI',
        to: '/edi',
        permission: 'edi.view',
        module: 'erp_integration',
      },
      {
        type: 'leaf',
        label: 'Imports',
        to: '/imports', module: 'import_engine',
        permission: 'imports.view',
      },
      {
        type: 'leaf',
        label: 'Exports',
        to: '/exports', module: 'export_engine',
        permission: 'exports.view',
      },
      {
        type: 'leaf',
        label: 'Accounting Exports',
        to: '/accounting-exports', module: 'accounting_sync',
        permission: 'accounting_exports.view',
      },
      {
        type: 'leaf',
        label: 'Scheduled Reports',
        to: '/scheduled-reports', module: 'scheduled_reports',
        permission: 'reports.view',
      },
      {
        type: 'leaf',
        label: 'Sync Admin',
        to: '/sync-admin',
        permission: 'sync.view',
      },
      {
        type: 'leaf',
        label: 'Payment Gateways',
        to: '/payment-gateways',
        permission: 'payment_gateways.view',
        moduleAny: ['gateway_jazzcash', 'gateway_easypaisa', 'gateway_stripe'],
      },
      {
        type: 'leaf',
        label: 'Storage Browser',
        to: '/admin/storage', module: 'storage',
        permission: 'settings.view',
      },
      {
        type: 'leaf',
        label: 'Customer Portal Sessions',
        to: '/admin/customer-portal-sessions', module: 'customers',
        permission: 'customers.update',
      },
      {
        type: 'leaf',
        label: 'Audit Logs',
        to: '/audit-logs', module: 'audit_query',
        icon: History,
        permission: 'settings.view',
      },
      {
        type: 'leaf',
        label: 'Plugins',
        to: '/plugins',
        icon: Puzzle,
        permission: 'plugins.view',
        module: 'plugins',
      },
      {
        type: 'leaf',
        label: 'Professional Services',
        to: '/professional-services',
        icon: Sparkles,
        permission: 'ps_requests.view',
        module: 'ps_engagements',
      },
      {
        type: 'leaf',
        label: 'Audit Log',
        to: '/audit',
        icon: ShieldCheck,
        permission: 'audit_query.view',
      },
      {
        type: 'leaf',
        label: 'Backups',
        to: '/admin/backups',
        icon: Briefcase,
        permission: 'backups.view',
      },
      {
        type: 'leaf',
        label: 'Custom Dashboards',
        to: '/admin/dashboards', module: 'custom_dashboards',
        icon: BarChart3,
        permission: 'reports.view',
      },
      {
        type: 'leaf',
        label: 'Invoice Templates',
        to: '/admin/invoice-templates', module: 'document_templates',
        icon: Briefcase,
        permission: 'document_templates.manage',
      },
    ],
  },
  {
    type: 'section',
    label: 'People (HR)',
    icon: UserCog,
    children: [
      {
        type: 'leaf',
        label: 'Employees',
        to: '/hr/employees',
        icon: Users,
        permission: 'hr.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Leave',
        to: '/hr/leave',
        icon: Timer,
        permission: 'leave.manage',
        module: 'leave',
      },
      {
        type: 'leaf',
        label: 'Org structure',
        to: '/hr/departments',
        icon: Building2,
        permission: 'hr.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Payroll',
        to: '/hr/payroll',
        icon: Banknote,
        permission: 'payroll.view',
        module: 'payroll',
      },
      {
        type: 'leaf',
        label: 'Overtime',
        to: '/hr/overtime',
        icon: Timer,
        permission: 'overtime.view',
        module: 'payroll',
      },
      {
        type: 'leaf',
        label: 'Loans',
        to: '/hr/loans',
        icon: Wallet,
        permission: 'loans.view',
        module: 'payroll',
      },
      {
        type: 'leaf',
        label: 'Onboarding',
        to: '/hr/onboarding',
        icon: FileCheck2,
        permission: 'onboarding.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Exit & settlement',
        to: '/hr/separations',
        icon: Receipt,
        permission: 'offboarding.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Benefits',
        to: '/hr/benefits',
        icon: ShieldCheck,
        permission: 'benefits.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'HR helpdesk',
        to: '/hr/helpdesk',
        icon: MessageSquare,
        permission: 'helpdesk.manage',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Recruitment',
        to: '/hr/recruitment',
        icon: Target,
        permission: 'recruitment.manage',
        module: 'recruitment',
      },
      {
        type: 'leaf',
        label: 'Performance',
        to: '/hr/performance',
        icon: BarChart3,
        permission: 'performance.manage',
        module: 'performance',
      },
      {
        type: 'leaf',
        label: 'Training',
        to: '/hr/training',
        icon: Sparkles,
        permission: 'performance.manage',
        module: 'performance',
      },
      {
        type: 'leaf',
        label: 'Disciplinary',
        to: '/hr/disciplinary',
        icon: ShieldCheck,
        permission: 'disciplinary.view',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Grievances',
        to: '/hr/grievances',
        icon: History,
        permission: 'grievance.manage',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Succession',
        to: '/hr/succession',
        icon: Users,
        permission: 'talent.view',
        module: 'performance',
      },
      {
        type: 'leaf',
        label: 'Self-service',
        to: '/hr/me',
        icon: UserCog,
        permission: 'ess.access',
        module: 'hr_core',
      },
      {
        type: 'leaf',
        label: 'Configuration',
        to: '/hr/config',
        icon: Settings,
        permission: 'hr.view',
        module: 'hr_core',
      },
    ],
  },
  {
    type: 'section',
    label: 'Account',
    icon: Briefcase,
    children: [
      {
        type: 'leaf',
        label: 'My Profile',
        to: '/profile',
        icon: Briefcase,
      },
      {
        type: 'leaf',
        label: 'Support',
        to: '/support',
        icon: LifeBuoy,
      },
    ],
  },
]
